/**
 * 나머지 기능 훅 — recipient/inquiry/notice/faq/comments/survey/reservation/store.
 * 모두 host React 사용. 공통 { loading, error } + 데이터 상태 패턴.
 */
import { getReact } from "./host";
import * as core from "../core/index";

function useAsync() {
  const React = getReact();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);
  async function run<T>(fn: () => Promise<T>): Promise<T | null> {
    setLoading(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e as Error);
      return null;
    } finally {
      setLoading(false);
    }
  }
  return { loading, error, run };
}

/** 결제 공통 훅 — fetchTerms()로 표준 구매약관을 받아 결제 전 표시+동의.
 *  커스텀 결제(#900): serverFn 이 만든 세션으로 beginWidget → 복귀 페이지에서 getRedirectResult → serverFn 이 승인. */
export function usePayment() {
  const React = getReact();
  const { loading, error, run } = useAsync();
  const fetchTerms = React.useCallback(() => run(() => core.getPurchaseTerms()), []);
  // store/reservation 의 beginWidgetCheckout 과 같이 run 으로 감싸지 않는다 — 실패를 throw 로 받아 앱이 처리한다.
  const beginWidget = React.useCallback(
    (session: core.CustomPaymentSession, params: core.PaymentWidgetParams) =>
      core.beginPaymentWidget(session, params),
    []
  );
  return { fetchTerms, beginWidget, getRedirectResult: core.getPaymentRedirectResult, loading, error };
}

export function useRecipient() {
  const React = getReact();
  const { loading, error, run } = useAsync();
  const register = React.useCallback(
    (input: core.RecipientInput) => run(() => core.registerRecipient(input)),
    []
  );
  return { register, loading, error };
}

/** 문의하기 — fetchConfig 로 접수 여부·동의 문구를 읽고(config state === 반환값), submit 은 state 없이 결과를 반환(useRecipient 와 동일). */
export function useInquiry() {
  const React = getReact();
  const { loading, error, run } = useAsync();
  const [config, setConfig] = React.useState<core.InquiryConfig | null>(null);
  const fetchConfig = React.useCallback(
    () => run(async () => { const d = await core.getInquiryConfig(); setConfig(d); return d; }),
    []
  );
  const submit = React.useCallback(
    (input: core.InquiryInput) => run(() => core.submitInquiry(input)),
    []
  );
  return { config, fetchConfig, submit, loading, error };
}

export function useNotice() {
  const React = getReact();
  const { loading, error, run } = useAsync();
  const [posts, setPosts] = React.useState<core.PostListResult | null>(null);
  const [post, setPost] = React.useState<core.BoardPost | null>(null);
  const fetchPosts = React.useCallback(
    (o: core.PostListOptions = {}) => run(async () => { const d = await core.listNoticePosts(o); setPosts(d); return d; }),
    []
  );
  const fetchPost = React.useCallback(
    (id: string) => run(async () => { const d = await core.getNoticePost(id); setPost(d); return d; }),
    []
  );
  return { posts, post, loading, error, fetchPosts, fetchPost };
}

export function useFaq() {
  const React = getReact();
  const { loading, error, run } = useAsync();
  const [posts, setPosts] = React.useState<core.PostListResult | null>(null);
  const [post, setPost] = React.useState<core.BoardPost | null>(null);
  const fetchPosts = React.useCallback(
    (o: core.PostListOptions = {}) => run(async () => { const d = await core.listFaqPosts(o); setPosts(d); return d; }),
    []
  );
  const fetchPost = React.useCallback(
    (id: string) => run(async () => { const d = await core.getFaqPost(id); setPost(d); return d; }),
    []
  );
  return { posts, post, loading, error, fetchPosts, fetchPost };
}

export function useComments() {
  const React = getReact();
  const { loading, error, run } = useAsync();
  const [comments, setComments] = React.useState<core.Comment[] | null>(null);
  const fetchComments = React.useCallback(
    (postId: string, sort = "latest") => run(async () => { const d = await core.listComments(postId, sort); setComments(d); return d; }),
    []
  );
  const addComment = React.useCallback(
    (postId: string, content: string) => run(() => core.createComment(postId, { content })),
    []
  );
  const editComment = React.useCallback(
    (postId: string, commentId: string, content: string) => run(() => core.updateComment(postId, commentId, { content })),
    []
  );
  const removeComment = React.useCallback(
    (postId: string, commentId: string) => run(() => core.deleteComment(postId, commentId)),
    []
  );
  return { comments, loading, error, fetchComments, addComment, editComment, removeComment };
}

export function useSurvey() {
  const React = getReact();
  const { loading, error, run } = useAsync();
  const [surveys, setSurveys] = React.useState<core.Survey[] | null>(null);
  const [survey, setSurvey] = React.useState<core.Survey | null>(null);
  // 반환값 === state (`surveys` 배열) — useStore.fetchProducts 와 동일 규약(v0.13.0).
  const fetchSurveys = React.useCallback(
    (params: Record<string, string> = {}) =>
      run(async () => {
        const items = (await core.listSurveys(params)).items ?? [];
        setSurveys(items);
        return items;
      }),
    []
  );
  const fetchSurvey = React.useCallback(
    (id: string) => run(async () => { const d = await core.getSurvey(id); setSurvey(d); return d; }),
    []
  );
  const submitResponse = React.useCallback(
    (id: string, answers: unknown) => run(() => core.submitSurveyResponse(id, answers)),
    []
  );
  return { surveys, survey, loading, error, fetchSurveys, fetchSurvey, submitResponse };
}

export function useReservation() {
  const React = getReact();
  const { loading, error, run } = useAsync();
  const [targets, setTargets] = React.useState<core.ReservationTarget[] | null>(null);
  const fetchTargets = React.useCallback(
    () => run(async () => { const d = await core.listTargets(); setTargets(d); return d; }),
    []
  );
  const fetchTarget = React.useCallback((id: string) => run(() => core.getTarget(id)), []);
  const fetchSlots = React.useCallback((id: string, p: Record<string, string>) => run(() => core.getAvailableSlots(id, p)), []);
  const fetchSlotRange = React.useCallback((id: string, p: Record<string, string>) => run(() => core.getSlotRange(id, p)), []);
  const book = React.useCallback((id: string, data: any) => run(() => core.createBooking(id, data)), []);
  const prepare = React.useCallback((id: string, data: any) => run(() => core.prepareBooking(id, data)), []);
  const confirm = React.useCallback((id: string, payload: any) => run(() => core.confirmBooking(id, payload)), []);
  const myBookings = React.useCallback((p: Record<string, string> = {}) => run(() => core.listMyBookings(p)), []);
  const cancel = React.useCallback((rid: string) => run(() => core.cancelBooking(rid)), []);
  // 예약 결제위젯(인라인). 앱이 셀렉터 제공 → handle.requestPayment 로 결제(앱 화면 유지).
  const beginWidgetCheckout = React.useCallback(
    (id: string, params: core.ReservationWidgetCheckoutParams) =>
      core.beginReservationWidgetCheckout(id, params),
    [],
  );
  return {
    targets, loading, error, fetchTargets, fetchTarget, fetchSlots, fetchSlotRange,
    book, prepare, confirm, beginWidgetCheckout, myBookings, cancel,
    getCheckoutContext: core.getReservationCheckoutContext,
    clearCheckoutContext: core.clearReservationCheckoutContext,
  };
}

export function useStore() {
  const React = getReact();
  const { loading, error, run } = useAsync();
  const [config, setConfig] = React.useState<core.StoreConfig | null>(null);
  const [products, setProducts] = React.useState<core.Product[] | null>(null);
  const fetchConfig = React.useCallback(() => run(async () => { const d = await core.getStoreConfig(); setConfig(d); return d; }), []);
  // 반환값 === state (`products` 배열). 코어는 백엔드 버전차 흡수를 위해 `{items}` 로 정규화하지만,
  // 훅 표면에서는 그 봉투를 벗겨 **state 와 같은 값**을 돌려준다(v0.13.0 — 이전엔 봉투를 반환해
  // `(await fetchProducts()).map(...)` 이 TypeError 였다).
  const fetchProducts = React.useCallback(
    (p: Record<string, string> = {}) =>
      run(async () => {
        const items = (await core.listProducts(p)).items ?? [];
        setProducts(items);
        return items;
      }),
    []
  );
  const fetchProduct = React.useCallback((id: string) => run(() => core.getProduct(id)), []);
  // 구매약관 조회는 결제 공통 훅으로 이동 → usePayment().fetchTerms
  const prepare = React.useCallback((productId: string, qty: number) => run(() => core.prepareOrder(productId, qty)), []);
  const confirm = React.useCallback((data: any) => run(() => core.confirmOrder(data)), []);
  const myOrders = React.useCallback((p: Record<string, string> = {}) => run(() => core.listMyOrders(p)), []);
  const confirmPurchase = React.useCallback((orderId: string) => run(() => core.confirmPurchase(orderId)), []);
  const cancel = React.useCallback((orderId: string, reason: string) => run(() => core.cancelOrder(orderId, reason)), []);
  // 결제위젯(인라인). 앱이 셀렉터 제공 → handle.requestPayment 로 결제(앱 화면 유지).
  const beginWidgetCheckout = React.useCallback(
    (params: core.StoreWidgetCheckoutParams) => core.beginStoreWidgetCheckout(params),
    [],
  );
  return {
    config, products, loading, error, fetchConfig, fetchProducts, fetchProduct,
    prepare, confirm, beginWidgetCheckout, myOrders, confirmPurchase, cancel,
    getCheckoutContext: core.getStoreCheckoutContext,
    clearCheckoutContext: core.clearStoreCheckoutContext,
  };
}
